import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import useUserStore, { IProject } from "common/store/UserStore";
import { Button, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import useProjectStore from "common/store/ProjectStore";
import ProjectTableHead, { Order } from "./ProjectTableHead";
import ProjectActionConfirmations from "./ProjectActionConfirmations";
import LoadingSpinnerBackdrop from "common/components/progress/LoadingSpinner";
import { LanguageContext } from "common/contexts/LanguageContext";
import useSettingsStore from "common/store/SettingsStore";
import useNotificationStore from "common/store/NotificationStore";
import { useAuthContext } from "common/contexts/AuthContext";
import CustomTablePagination from "./CustomTablePagination";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === "updated" || orderBy === "created") {
    const dateA = new Date(a[orderBy] as string);
    const dateB = new Date(b[orderBy] as string);
    if (dateB < dateA) {
      return -1;
    }
    if (dateB > dateA) {
      return 1;
    }
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string | Date }, b: { [key in Key]: number | string | Date }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const ProjectList: FC = () => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IProject>("updatedDate");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [filter, setFilter] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<IProject[]>([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
  const [isRenameConfirmOpen, setIsRenameConfirmOpen] = useState<boolean>(false);
  const [anchorActionsEl, setAnchorActionsEl] = useState<HTMLButtonElement | null>(null);
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);
  const [actionProject, setActionProject] = useState<IProject | null>(null);
  const [isUnshareConfirmOpen, setIsUnshareConfirmOpen] = useState(false);
  const [isSharedProjectInfoOpen, setIsSharedProjectInfoOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);

  const { fetchProjects, projects, copyProject, isBusy, unshareProject } = useUserStore();
  const { setProjectID } = useProjectStore();
  const { settings } = useSettingsStore();
  const { enqueueNotification } = useNotificationStore();
  const { getString, selectedLanguageLocale } = useContext(LanguageContext);
  const { getAccessToken } = useAuthContext();

  const history = useHistory();

  useEffect(() => {
    if (settings.apiBaseUrl !== "") {
      fetchProjects(settings.apiBaseUrl, getAccessToken);
      setProjectID(null);
    }
  }, [fetchProjects, setProjectID, settings, getAccessToken]);

  useEffect(() => {
    const fltrRows = projects.filter(
      (row) => filter.length === 0 || row.projectName.toLowerCase().includes(filter.toLowerCase()),
    );
    setFilteredRows(fltrRows);
  }, [filter, projects]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IProject) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>, project: IProject) => {
    setAnchorActionsEl(event.currentTarget);
    setActionProject(project);
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
    setActionProject(null);
  };

  const handleDeleteMenuClick = () => {
    setActionsOpen(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleRenameMenuClick = () => {
    setActionsOpen(false);
    setIsRenameConfirmOpen(true);
  };

  const handleUnshareMenuClick = () => {
    setActionsOpen(false);
    setIsUnshareConfirmOpen(true);
  };

  const handleUnshareConfirmation = async () => {
    setIsUnshareConfirmOpen(false);
    const response = await unshareProject(
      settings.apiBaseUrl,
      getAccessToken,
      actionProject!.projectID,
      actionProject!.sharedUserID,
      actionProject!.sharedRowVersion,
    );
    if (!response.succeeded) {
      enqueueNotification("unshare_project_failure", getString("msg_unshare_proj_failed"), "error");
      return;
    }
    // Need to refetch projects to update project list which will now exclude the unshared project
    const resp2 = await fetchProjects(settings.apiBaseUrl, getAccessToken);
    if (!resp2.succeeded) {
      enqueueNotification("load_projects_failure", getString("msg_load_projects_failed"), "error");
      return;
    }
  };

  const handleCopyMenuClick = async () => {
    setActionsOpen(false);
    const response = await copyProject(settings.apiBaseUrl, getAccessToken, actionProject!);
    if (response.succeeded) {
      enqueueNotification("copy_project_success", getString("tbl_proj_copy_success"), "success");
    } else {
      enqueueNotification("copy_project_failure", response.errorMessage!, "error");
    }
  };

  // handle project click
  function navigateToProject(projectID: number) {
    setProjectID(null);
    history.push(`/${selectedLanguageLocale}/project/${projectID}`);
  }

  const handleProjectClick = (project: IProject) => {
    setSelectedProject(project);
    if (project.sharedUserID > 0) {
      setIsSharedProjectInfoOpen(true);
    } else {
      navigateToProject(project.projectID);
    }
  };

  const handleProjectInfoClose = () => {
    setIsSharedProjectInfoOpen(false);
    if (selectedProject) {
      navigateToProject(selectedProject.projectID);
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      filteredRows
        .slice()
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows],
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString();
    return formattedDate;
  };

  return (
    <>
      {isBusy && <LoadingSpinnerBackdrop />}
      <Box sx={{ width: "100%" }}>
        <TextField
          sx={{ width: "55%" }}
          placeholder={getString("tbl_proj_search")}
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        />
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
              <ProjectTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={filteredRows.length}
              />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow role="checkbox" tabIndex={-1} key={row.projectID} sx={{ cursor: "pointer" }}>
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        <Button
                          sx={{
                            height: "100%",
                            width: "100%",
                            ":hover": {
                              color: "secondary.main",
                              backgroundColor: "initial",
                            },
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                          onClick={() => handleProjectClick(row)}
                        >
                          <Typography variant="headermenu">{row.projectName}</Typography>
                        </Button>
                      </TableCell>
                      <TableCell align="center">{getString(row.owner)}</TableCell>
                      <TableCell align="center">{row.networkCount}</TableCell>
                      <TableCell align="center">{formatDate(row.updatedDate.toString())}</TableCell>
                      <TableCell align="center">{formatDate(row.createdDate.toString())}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={(event) => handleActionsClick(event, row)}
                          sx={{
                            ":hover": {
                              color: "secondary.main",
                              backgroundColor: "initial",
                            },
                          }}
                        >
                          <MoreHorizIcon fontSize="small" />
                        </IconButton>
                        <Menu anchorEl={anchorActionsEl} open={actionsOpen} onClose={handleActionsClose}>
                          <MenuItem onClick={() => handleDeleteMenuClick()}>{getString("cmn_delete")}</MenuItem>
                          <MenuItem onClick={() => handleCopyMenuClick()}>{getString("cmn_copy")}</MenuItem>
                          <MenuItem onClick={() => handleRenameMenuClick()}>{getString("cmn_rename")}</MenuItem>
                          {actionProject && actionProject.sharedUserID > 0 && (
                            <MenuItem onClick={() => handleUnshareMenuClick()}>{getString("cmn_unshare")}</MenuItem>
                          )}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 53 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        {actionProject && (
          <ProjectActionConfirmations
            project={actionProject}
            isDeleteConfirmOpen={isDeleteConfirmOpen}
            setIsDeleteConfirmOpen={setIsDeleteConfirmOpen}
            isRenameConfirmOpen={isRenameConfirmOpen}
            setIsRenameConfirmOpen={setIsRenameConfirmOpen}
          />
        )}
      </Box>
      <ConfirmationModal
        isOpen={isUnshareConfirmOpen}
        handleClose={() => {
          setIsUnshareConfirmOpen(false);
        }}
        handleConfirm={handleUnshareConfirmation}
        title={getString("dlg_unshare_confirm_title")}
        maxWidth="sm"
        hideDivider={true}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {getString("dlg_unshare_confirm_body")}
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isSharedProjectInfoOpen}
        handleClose={handleProjectInfoClose}
        handleConfirm={handleProjectInfoClose}
        title={getString("dlg_sharing_title")}
        maxWidth="sm"
        hideDivider={true}
        hideCancel={true}
        confirmBtnLabel={getString("cmn_ok")}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {getString("dlg_sharing_body")}
        </Typography>
      </ConfirmationModal>
    </>
  );
};

export default ProjectList;
